/**
 * ComparePanel (#140): the Reader side panel "Compare against" — one
 * language, several countries, NACE codes, other ready collection documents
 * (never the open one) — plus Compare and "Todos for this document". Compare
 * needs a language, a country and a NACE code (an empty selection makes no
 * sense). The last selection of the document is pre-filled from listTodos,
 * minus codes the current facets no longer offer.
 * Compare starts a job and polls its status every pollMs until done; destroy
 * stops polling. The running job is kept by the host (getRunningJob /
 * setRunningJob), so a panel mounted again resumes it: Compare stays disabled
 * and, when the job finishes, onDone runs only if its document is still the
 * open one — otherwise onFinishedElsewhere. A session that ends while polling
 * surfaces through the transport's signed-out handling (the request stays
 * pending).
 */

import template from './compare-panel.template.html?raw';
import type { TranslationParams } from '../i18n/ui-manager.types';
import type CompareService from '../core/compare-service';
import type { CompareJob, Facets, Selection } from '../core/compare-service';
import type FilesService from '../core/files-service';
import type { FileItem } from '../core/files-service';
import { createNaceTree, type NaceTree } from './nace-tree';
import { createChipSelect, type ChipSelect } from './chip-select';
import { errorMessage } from '../core/forbidden-message';
import { GatewayErrorCode, isGatewayError } from '../core/gateway/error-codes';

export interface ComparePanelOptions {
  t: (key: string, params?: TranslationParams) => string;
  compare: CompareService;
  files: FilesService;
  getDocument(): { id: string; name: string } | null;
  /** Show the document's todos: after a finished compare and from the Todos button. */
  onDone(documentId: string): void;
  /** The compare job still running for this organisation, kept across mounts. */
  getRunningJob?(): RunningCompare | null;
  setRunningJob?(job: RunningCompare | null): void;
  /** A compare finished while another document (or none) is open. */
  onFinishedElsewhere?(documentId: string): void;
  pollMs?: number;
}

export interface RunningCompare {
  documentId: string;
  jobId: string;
}

export interface ComparePanel {
  readonly element: HTMLElement;
  refresh(): Promise<void>;
  /** Labels in the current UI language; picks and filters stay. */
  retranslate(): void;
  destroy(): void;
}

const SEARCH_DEBOUNCE_MS = 300;

let idCounter = 0;

export function createComparePanel(opts: ComparePanelOptions): ComparePanel {
  const { t, compare, files, getDocument, onDone } = opts;
  const setRunningJob = (job: RunningCompare | null) =>
    opts.setRunningJob?.(job);
  const pollMs = opts.pollMs ?? 2000;

  const element = document.createElement('div');
  element.className = 'compare-panel';
  element.innerHTML = template;
  const translateStatic = () => {
    for (const node of element.querySelectorAll<HTMLElement>(
      '[data-compare-text]'
    )) {
      node.textContent = t(node.dataset.compareText!);
    }
    search.placeholder = t('compare.panel.collectionSearch');
    search.setAttribute('aria-label', t('compare.panel.collectionSearch'));
  };
  const $ = <T extends HTMLElement>(selector: string) =>
    element.querySelector<T>(selector)!;

  const languageSelect = $<HTMLSelectElement>('[data-compare-language]');
  const languageId = `compare-language-${++idCounter}`;
  languageSelect.id = languageId;
  $<HTMLLabelElement>('[data-compare-language-label]').htmlFor = languageId;
  const countriesHost = $('[data-compare-countries]');
  const naceHost = $('[data-compare-nace]');
  const search = $<HTMLInputElement>('[data-compare-collection-search]');
  const docList = $('[data-compare-collection]');
  const docEmpty = $('[data-compare-collection-empty]');
  const docMore = $('[data-compare-collection-more]');
  const docSelected = $('[data-compare-collection-selected]');
  const startButton = $<HTMLButtonElement>('[data-compare-start]');
  const todosButton = $<HTMLButtonElement>('[data-compare-todos]');
  const message = $('[data-compare-message]');
  translateStatic();

  let facets: Facets | null = null;
  let language = '';
  let countries: string[] = [];
  let nace: string[] = [];
  const chosenDocs = new Set<string>();
  let docs: FileItem[] = [];
  let chips: ChipSelect | null = null;
  let tree: NaceTree | null = null;
  let running = false;
  let destroyed = false;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  let collectionRequest = 0;
  let shownDocumentId: string | null = null;
  /** Open todos of the shown document; null before they are known. */
  let openTodoCount: number | null = null;

  const say = (text: string) => {
    message.textContent = text;
  };
  const openId = () => getDocument()?.id ?? null;
  const showTodos = () => {
    todosButton.textContent =
      openTodoCount === null
        ? t('compare.panel.todos')
        : t('compare.panel.todosCount', { count: openTodoCount });
  };

  const update = () => {
    const ready = language !== '' && countries.length > 0 && nace.length > 0;
    startButton.disabled = running || !openId() || !ready;
    todosButton.disabled = !openId();
  };

  const buildControls = (data: Facets) => {
    languageSelect.replaceChildren();
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = t('compare.panel.languagePlaceholder');
    languageSelect.append(placeholder);
    for (const option of data.languages) {
      const el = document.createElement('option');
      el.value = option.code;
      el.textContent = option.label;
      languageSelect.append(el);
    }
    languageSelect.value = language;
    chips = createChipSelect({
      t,
      options: data.countries,
      selected: countries,
      addLabelKey: 'compare.addCountry',
      onChange: (codes) => {
        countries = codes;
        update();
      },
    });
    countriesHost.replaceChildren(chips.element);
    tree = createNaceTree({
      t,
      sections: data.nace,
      selected: nace,
      onChange: (codes) => {
        nace = codes;
        update();
      },
    });
    naceHost.replaceChildren(tree.element);
  };

  languageSelect.addEventListener('change', () => {
    language = languageSelect.value;
    update();
  });

  // Counts picks the current search hides too: they go into Compare as well.
  const showSelected = () => {
    docSelected.hidden = chosenDocs.size === 0;
    docSelected.textContent = t('compare.panel.collectionSelected', {
      count: chosenDocs.size,
    });
  };

  const renderDocs = () => {
    const open = openId();
    const visible = docs.filter((item) => item.id !== open);
    docList.replaceChildren();
    for (const item of visible) {
      const li = document.createElement('li');
      const label = document.createElement('label');
      label.className = 'compare-panel__doc';
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.dataset.compareDoc = item.id;
      input.checked = chosenDocs.has(item.id);
      input.addEventListener('change', () => {
        if (input.checked) chosenDocs.add(item.id);
        else chosenDocs.delete(item.id);
        showSelected();
      });
      const name = document.createElement('span');
      name.textContent = item.name;
      label.append(input, name);
      if (item.path) {
        const folder = document.createElement('span');
        folder.className = 'compare-panel__doc-path';
        folder.textContent = item.path;
        label.append(folder);
      }
      li.append(label);
      docList.append(li);
    }
    docEmpty.hidden = visible.length > 0;
    showSelected();
  };

  const loadCollection = async () => {
    const request = ++collectionRequest;
    const q = search.value.trim();
    try {
      const result = await files.listCollection({
        status: 'ready',
        ...(q ? { q } : {}),
      });
      if (destroyed || request !== collectionRequest) return;
      docs = result.items;
      docMore.hidden = result.total <= result.items.length;
      renderDocs();
    } catch (error) {
      if (destroyed || request !== collectionRequest) return;
      say(errorText(error));
    }
  };

  search.addEventListener('input', () => {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      searchTimer = null;
      void loadCollection();
    }, SEARCH_DEBOUNCE_MS);
  });

  const applySelection = (selection: Selection, documentId: string) => {
    // Codes the facets no longer offer never make Compare look ready.
    const languages = new Set(facets?.languages.map((o) => o.code) ?? []);
    const countryCodes = new Set(facets?.countries.map((o) => o.code) ?? []);
    const naceCodes = new Set(
      (facets?.nace ?? []).flatMap((section) => [
        section.code,
        ...section.divisions.map((division) => division.code),
      ])
    );
    language = languages.has(selection.language) ? selection.language : '';
    countries = selection.countries.filter((code) => countryCodes.has(code));
    nace = selection.nace.filter((code) => naceCodes.has(code));
    chosenDocs.clear();
    for (const id of selection.documentIds) {
      if (id !== documentId) chosenDocs.add(id);
    }
    languageSelect.value = language;
    chips?.setSelected(countries);
    tree?.setSelected(nace);
    renderDocs();
  };

  const loadTodos = async () => {
    const open = getDocument();
    if ((open?.id ?? null) !== shownDocumentId) {
      // Another document: language, countries and NACE stay (convenience),
      // but collection picks from the previous one never ride along unseen.
      shownDocumentId = open?.id ?? null;
      chosenDocs.clear();
      openTodoCount = null;
      renderDocs();
    }
    if (!open) {
      showTodos();
      return;
    }
    try {
      const [last, openTodos] = await Promise.all([
        compare.listTodos({ documentId: open.id }),
        compare.listTodos({ documentId: open.id, state: 'open' }),
      ]);
      if (destroyed || getDocument()?.id !== open.id) return;
      if (last.selection) applySelection(last.selection, open.id);
      openTodoCount = openTodos.items.length;
      showTodos();
    } catch (error) {
      if (!destroyed) say(errorText(error));
    }
  };

  const errorText = (error: unknown) =>
    errorMessage(error, (key) => t(key), t('compare.panel.error'));

  const wait = () =>
    new Promise<void>((resolve) => {
      pollTimer = setTimeout(() => {
        pollTimer = null;
        resolve();
      }, pollMs);
    });

  /** Poll a job until it ends; `current` is null when resuming one. */
  const follow = async (job: RunningCompare, current: CompareJob | null) => {
    while (!destroyed) {
      if (current?.status === 'done') {
        setRunningJob(null);
        say('');
        if (getDocument()?.id === job.documentId) onDone(job.documentId);
        else opts.onFinishedElsewhere?.(job.documentId);
        return;
      }
      if (current?.status === 'failed') {
        setRunningJob(null);
        say(t('compare.panel.failed'));
        return;
      }
      if (current) {
        await wait();
        if (destroyed) return;
      }
      current = await compare.status(job.jobId);
    }
  };

  const track = async (
    begin: () => Promise<{ job: RunningCompare; current: CompareJob | null }>
  ) => {
    running = true;
    update();
    say(t('compare.panel.comparing'));
    let job: RunningCompare | null = null;
    try {
      const started = await begin();
      job = started.job;
      if (destroyed) return;
      await follow(job, started.current);
    } catch (error) {
      if (destroyed) return;
      if (job) setRunningJob(null);
      say(
        isGatewayError(error, GatewayErrorCode.COMPARE_RUNNING)
          ? t('compare.panel.running')
          : errorText(error)
      );
    } finally {
      running = false;
      if (!destroyed) update();
    }
  };

  const runCompare = () => {
    const open = getDocument();
    if (!open || startButton.disabled) return Promise.resolve();
    return track(async () => {
      const current = await compare.start({
        documentId: open.id,
        language,
        countries: [...countries],
        nace: [...nace],
        documentIds: [...chosenDocs].filter((id) => id !== open.id),
      });
      const job = { documentId: open.id, jobId: current.jobId };
      setRunningJob(job);
      return { job, current };
    });
  };

  const resume = () => {
    if (running) return;
    const job = opts.getRunningJob?.() ?? null;
    if (job) void track(async () => ({ job, current: null }));
  };

  startButton.addEventListener('click', () => void runCompare());
  todosButton.addEventListener('click', () => {
    const id = openId();
    if (id) onDone(id);
  });

  return {
    element,
    async refresh() {
      resume();
      if (!facets) {
        try {
          facets = await compare.facets();
          if (destroyed) return;
          buildControls(facets);
        } catch (error) {
          if (!destroyed) say(errorText(error));
        }
      }
      if (destroyed) return;
      await Promise.all([loadCollection(), loadTodos()]);
      if (!destroyed) update();
    },
    retranslate() {
      translateStatic();
      if (facets) buildControls(facets);
      showSelected();
      showTodos();
    },
    destroy() {
      destroyed = true;
      if (pollTimer) clearTimeout(pollTimer);
      if (searchTimer) clearTimeout(searchTimer);
      pollTimer = null;
      searchTimer = null;
    },
  };
}
